import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatDayMonth } from '../../utils/dateLocale';
import { RootState } from '../../redux/store';
import { PlannedMeal, markIngredientsReviewed } from '../../redux/mealPlanSlice';
import { addCustomItem } from '../../redux/groceryListSlice';
import { GroceryCategory } from '../../types/groceryList';
import { Paprika } from '../../styles/theme';
import { RecipePlaceholder } from '../shared/RecipePlaceholder';
import {
  getInventoryMatchesForRecipe,
  ingredientMatchKey,
} from '../../services/inventoryMatchService';
import { addItemsToPantryAndSync } from '../../redux/inventorySlice';
import { getNewPantryItemNamesFromReview } from './ingredientReviewPantryUtils';
import { RemoteImage } from '../shared/RemoteImage';

interface IngredientReviewSheetProps {
  visible: boolean;
  meal: PlannedMeal | null;
  onClose: () => void;
}

interface IngredientItem {
  id: string;
  name: string;
  original: string;
  checked: boolean;
  inInventory: boolean; // True if auto-matched from inventory
  inventoryItemName?: string;
}

export const IngredientReviewSheet: React.FC<IngredientReviewSheetProps> = ({
  visible,
  meal,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const inventoryItems = useSelector((state: RootState) => state.inventory?.items ?? []);
  const { user } = useSelector((state: RootState) => state.auth);

  // Match recipe ingredients against inventory
  const inventoryMatches = useMemo(() => {
    if (!meal?.recipe?.extendedIngredients) return new Map();
    return getInventoryMatchesForRecipe(meal.recipe.extendedIngredients, inventoryItems);
  }, [meal, inventoryItems]);

  // Initialize ingredients from meal recipe, pre-checking inventory matches
  const initialIngredients = useMemo((): IngredientItem[] => {
    if (!meal?.recipe?.extendedIngredients) return [];

    return meal.recipe.extendedIngredients.map((ing, idx) => {
      // Same key the map was built with — see ingredientMatchKey (#601).
      const ingredientName = ingredientMatchKey(ing) || 'Unknown ingredient';
      const match = inventoryMatches.get(ingredientName);
      const inInventory = match?.matched ?? false;

      return {
        id: `${ing.id || idx}`,
        name: ingredientName,
        original: ing.original || ing.name || '',
        checked: inInventory, // Pre-check items found in inventory
        inInventory,
        inventoryItemName: match?.inventoryItemName,
      };
    });
  }, [meal, inventoryMatches]);

  const [ingredients, setIngredients] = useState<IngredientItem[]>(initialIngredients);

  // Reset ingredients when meal changes
  React.useEffect(() => {
    setIngredients(initialIngredients);
  }, [initialIngredients]);

  const toggleIngredient = useCallback((id: string) => {
    setIngredients(prev =>
      prev.map(ing =>
        ing.id === id ? { ...ing, checked: !ing.checked } : ing
      )
    );
  }, []);

  const handleConfirm = useCallback(() => {
    if (!meal) return;

    // Get unchecked ingredients (items user doesn't have = missing)
    const missingIngredients = ingredients
      .filter(ing => !ing.checked)
      .map(ing => ing.name);

    // Dispatch mark as reviewed
    dispatch(markIngredientsReviewed({
      mealId: meal.id,
      missingIngredients,
    }));

    // Add missing ingredients to grocery list
    missingIngredients.forEach(ingredientName => {
      dispatch(addCustomItem({
        name: ingredientName,
        category: GroceryCategory.Produce, // Default category, could be smarter
      }));
    });

    // Silently add checked non-inventory items to pantry (with cloud sync)
    const newPantryNames = getNewPantryItemNamesFromReview(ingredients);
    if (newPantryNames.length > 0) {
      dispatch(addItemsToPantryAndSync({ ingredientNames: newPantryNames, userId: user?.uid }) as any);
    }

    onClose();
  }, [meal, ingredients, dispatch, onClose, user]);

  const checkedCount = ingredients.filter(ing => ing.checked).length;
  const missingCount = ingredients.length - checkedCount;
  const inventoryMatchCount = ingredients.filter(ing => ing.inInventory).length;
  const uncheckedInventoryCount = ingredients.filter(ing => ing.inInventory && !ing.checked).length;

  const handleConfirmInventoryMatches = useCallback(() => {
    setIngredients(prev =>
      prev.map(ing =>
        ing.inInventory ? { ...ing, checked: true } : ing
      )
    );
  }, []);

  if (!meal) return null;

  const recipe = meal.recipe;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          {/* Header with drag indicator */}
          <View style={styles.dragIndicator} />

          {/* Recipe Header */}
          <View style={styles.recipeHeader}>
            <RemoteImage
              uri={recipe?.image}
              variant="thumb"
              style={styles.recipeImage}
              resizeMode="cover"
              fallback={
                <RecipePlaceholder
                  recipe={{
                    title: recipe?.title,
                    ingredients: recipe?.extendedIngredients?.map(i => i.name) ?? [],
                    diets: recipe?.diets,
                  }}
                  size={80}
                  style={styles.recipePlaceholder}
                  testID="ingredient-review-placeholder"
                />
              }
            />
            <View style={styles.recipeTitleContainer}>
              <Text style={styles.recipeTitle} numberOfLines={2}>
                {recipe?.title || t('thisWeek.unknownRecipe')}
              </Text>
              <Text style={styles.mealInfo}>
                {t(`thisWeek.mealTypes.${meal.mealType}`)} • {formatDayMonth(meal.scheduledFor, i18n.language)}
              </Text>
            </View>
          </View>

          {/* Instructions */}
          <View style={styles.instructions}>
            <Text style={styles.instructionsText}>{t('ingredientReview.checkHave')}</Text>
            <Text style={styles.instructionsSubtext}>{t('ingredientReview.uncheckedToList')}</Text>
            {inventoryMatchCount > 0 && (
              <Text style={styles.instructionsInventory}>
                {t('ingredientReview.foundInInventory', { count: inventoryMatchCount })}
              </Text>
            )}
          </View>

          {/* Quick Confirm Inventory Matches */}
          {uncheckedInventoryCount > 0 && (
            <TouchableOpacity
              style={styles.quickConfirmButton}
              onPress={handleConfirmInventoryMatches}
              activeOpacity={0.7}
            >
              <Text style={styles.quickConfirmText}>
                {t('ingredientReview.confirmMatches', { count: uncheckedInventoryCount })}
              </Text>
            </TouchableOpacity>
          )}

          {/* Ingredient List */}
          <ScrollView style={styles.ingredientList} showsVerticalScrollIndicator={false}>
            {ingredients.map(ingredient => (
              <TouchableOpacity
                key={ingredient.id}
                style={[
                  styles.ingredientRow,
                  ingredient.checked && styles.ingredientRowChecked,
                ]}
                onPress={() => toggleIngredient(ingredient.id)}
                activeOpacity={0.7}
              >
                <View style={[
                  styles.checkbox,
                  ingredient.checked && styles.checkboxChecked,
                ]}>
                  {ingredient.checked && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
                </View>
                <View style={styles.ingredientNameContainer}>
                  <Text style={[
                    styles.ingredientName,
                    ingredient.checked && styles.ingredientNameChecked,
                  ]}>
                    {ingredient.original || ingredient.name}
                  </Text>
                  {ingredient.inInventory && (
                    <View style={styles.inventoryBadge}>
                      <Text style={styles.inventoryBadgeText}>{t('ingredientReview.inInventory')}</Text>
                    </View>
                  )}
                </View>
                {!ingredient.checked && !ingredient.inInventory && (
                  <View style={styles.addToListBadge}>
                    <Text style={styles.addToListText}>{t('ingredientReview.plusList')}</Text>
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Summary */}
          <View style={styles.summary}>
            <Text style={styles.summaryText}>
              <Text style={styles.summaryHave}>{t('ingredientReview.haveCount', { count: checkedCount })}</Text>
              {' • '}
              <Text style={styles.summaryMissing}>{t('ingredientReview.needCount', { count: missingCount })}</Text>
            </Text>
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onClose}
            >
              <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.confirmButton}
              onPress={handleConfirm}
            >
              <Text style={styles.confirmButtonText}>{t('ingredientReview.markReviewed')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '85%',
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
  },
  dragIndicator: {
    width: 40,
    height: 4,
    backgroundColor: '#DDD',
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 16,
  },
  recipeHeader: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    marginBottom: 16,
  },
  recipeImage: {
    width: 80,
    height: 80,
    borderRadius: 12,
  },
  recipePlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 12,
  },
  recipeTitleContainer: {
    flex: 1,
    marginLeft: 16,
    justifyContent: 'center',
  },
  recipeTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: Paprika.textPrimary,
    marginBottom: 4,
  },
  mealInfo: {
    fontSize: 14,
    color: '#52443c',
  },
  instructions: {
    paddingHorizontal: 20,
    marginBottom: 12,
    backgroundColor: Paprika.primary + '10',
    paddingVertical: 12,
    marginHorizontal: 20,
    borderRadius: 12,
  },
  instructionsText: {
    fontSize: 14,
    fontWeight: '600',
    color: Paprika.primary,
  },
  instructionsSubtext: {
    fontSize: 12,
    color: '#52443c',
    marginTop: 2,
  },
  instructionsInventory: {
    fontSize: 12,
    color: Paprika.success || '#4CAF50',
    fontWeight: '600',
    marginTop: 4,
  },
  quickConfirmButton: {
    marginHorizontal: 20,
    marginBottom: 12,
    paddingVertical: 10,
    backgroundColor: '#E8F5E9',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#C8E6C9',
    alignItems: 'center',
  },
  quickConfirmText: {
    fontSize: 14,
    fontWeight: '600',
    color: Paprika.success || '#4CAF50',
  },
  ingredientList: {
    paddingHorizontal: 20,
    maxHeight: 300,
  },
  ingredientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 12,
    marginBottom: 8,
    backgroundColor: '#FFF5F5',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FFE0E0',
  },
  ingredientRowChecked: {
    backgroundColor: '#F0FFF0',
    borderColor: '#C0E0C0',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#CCC',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  checkboxChecked: {
    backgroundColor: Paprika.success || '#4CAF50',
    borderColor: Paprika.success || '#4CAF50',
  },
  checkmark: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  ingredientNameContainer: {
    flex: 1,
  },
  ingredientName: {
    fontSize: 15,
    color: Paprika.textPrimary,
  },
  ingredientNameChecked: {
    color: '#52443c',
    textDecorationLine: 'line-through',
  },
  inventoryBadge: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  inventoryBadgeText: {
    fontSize: 11,
    color: Paprika.success,
    fontWeight: '600',
  },
  addToListBadge: {
    backgroundColor: '#FF9800',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  addToListText: {
    fontSize: 11,
    color: '#ffffff',
    fontWeight: '600',
  },
  summary: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#EEE',
  },
  summaryText: {
    fontSize: 14,
    textAlign: 'center',
  },
  summaryHave: {
    color: Paprika.success || '#4CAF50',
    fontWeight: '600',
  },
  summaryMissing: {
    color: '#FF9800',
    fontWeight: '600',
  },
  actions: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: '#F0F0F0',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#52443c',
  },
  confirmButton: {
    flex: 2,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: Paprika.primary,
    alignItems: 'center',
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#ffffff',
  },
});

export default IngredientReviewSheet;
